import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, mobileApi, type ConnectPolicy, type MobileDeviceCommand, type MobileDeviceQuery, type MobilePolicy, type UpsertDistributionListPayload, type UpsertDomainPayload, type UpsertSamlIdpPayload, type UpsertSamlSpPayload, type UpsertTenantPayload, type UpsertTenantUserPayload } from "./adminBff";
import { useAccessToken } from "@/auth/useAccessToken";

export function useOverview() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "overview", token],
        queryFn: () => adminApi.overview(token),
        enabled: Boolean(token)
    });
}

export function useMe() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "me", token],
        queryFn: () => adminApi.me(token),
        enabled: Boolean(token)
    });
}

export function useSetDomainSeatLimit(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: {
            domainId: string;
            seatLimit: number | null;
            planTier: string | null;
            mailQuotaBytesPerUser?: number | null;
            fileQuotaBytesPerUser?: number | null;
            enabledApps?: string | null;
        }) =>
            adminApi.setDomainSeatLimit(
                tenantId, args.domainId, args.seatLimit, args.planTier, token,
                args.mailQuotaBytesPerUser, args.fileQuotaBytesPerUser, args.enabledApps),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "domains", tenantId] })
    });
}

export function useSetDomainSecurity(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { domainId: string; accessAllowedCidrs: string; allowImapSmtp: boolean; mfaMethods: string; requireMfa: boolean }) =>
            adminApi.setDomainSecurity(tenantId, args.domainId, args.accessAllowedCidrs, args.allowImapSmtp, args.mfaMethods, args.requireMfa, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "domains", tenantId] })
    });
}

export function useTenants() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "tenants", token],
        queryFn: () => adminApi.listTenants(token),
        enabled: Boolean(token)
    });
}

const invalidateTenants = (qc: ReturnType<typeof useQueryClient>) => {
    void qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
    void qc.invalidateQueries({ queryKey: ["admin", "overview"] });
};

export function useCreateTenant() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertTenantPayload) => adminApi.createTenant(p, token),
        onSuccess: () => invalidateTenants(qc)
    });
}

export function useUpdateTenant() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertTenantPayload }) =>
            adminApi.updateTenant(args.id, args.payload, token),
        onSuccess: () => invalidateTenants(qc)
    });
}

export function useTenantLifecycle() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; action: "suspend" | "reactivate" | "delete" }) => {
            switch (args.action) {
                case "suspend":
                    return adminApi.suspendTenant(args.id, token);
                case "reactivate":
                    return adminApi.reactivateTenant(args.id, token);
                case "delete":
                    return adminApi.deleteTenant(args.id, token);
            }
        },
        onSuccess: () => invalidateTenants(qc)
    });
}

export function useTenantUsers(tenantId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "users", tenantId, token],
        queryFn: () => (tenantId ? adminApi.listUsers(tenantId, token) : Promise.resolve([])),
        enabled: Boolean(tenantId) && Boolean(token)
    });
}

export function useCreateTenantUser(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertTenantUserPayload) => adminApi.createUser(tenantId, p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "users", tenantId] })
    });
}

export function useUpdateTenantUser(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { userId: string; payload: UpsertTenantUserPayload }) =>
            adminApi.updateUser(tenantId, args.userId, args.payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "users", tenantId] })
    });
}

export function useDeleteTenantUser(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (userId: string) => adminApi.deleteUser(tenantId, userId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "users", tenantId] })
    });
}

export function useDomains(tenantId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "domains", tenantId, token],
        queryFn: () => (tenantId ? adminApi.listDomains(tenantId, token) : Promise.resolve([])),
        enabled: Boolean(tenantId) && Boolean(token)
    });
}

export function useCreateDomain(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertDomainPayload) => adminApi.createDomain(tenantId, p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "domains", tenantId] })
    });
}

export function useVerifyDomain(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (domainId: string) => adminApi.verifyDomain(tenantId, domainId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "domains", tenantId] })
    });
}

export function useDeleteDomain(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (domainId: string) => adminApi.deleteDomain(tenantId, domainId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "domains", tenantId] })
    });
}

// Distribution lists — domain-scoped; the BFF confines TenantAdmins to
// their own domain server-side regardless of the filter.

export function useDistributionLists(domain: string | undefined) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "distribution-lists", domain ?? "all", token],
        queryFn: () => adminApi.listDistributionLists(domain, token),
        enabled: Boolean(token)
    });
}

export function useDlDomains() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "dl-domains", token],
        queryFn: () => adminApi.listDlDomains(token),
        enabled: Boolean(token)
    });
}

const invalidateDls = (qc: ReturnType<typeof useQueryClient>) => {
    void qc.invalidateQueries({ queryKey: ["admin", "distribution-lists"] });
    void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
};

export function useCreateDistributionList() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertDistributionListPayload) => adminApi.createDistributionList(p, token),
        onSuccess: () => invalidateDls(qc)
    });
}

export function useUpdateDistributionList() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertDistributionListPayload }) =>
            adminApi.updateDistributionList(args.id, args.payload, token),
        onSuccess: () => invalidateDls(qc)
    });
}

export function useDeleteDistributionList() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => adminApi.deleteDistributionList(id, token),
        onSuccess: () => invalidateDls(qc)
    });
}

export function useAudit(tenantId?: string) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "audit", tenantId, token],
        queryFn: () => adminApi.listAudit(tenantId, token),
        enabled: Boolean(token)
    });
}

export function useTenantStorage(tenantId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "storage", tenantId, token],
        // Refresh every 30 s — MinIO listing is cheap and the dashboard
        // is effectively a snapshot of a write-heavy bucket.
        refetchInterval: 30_000,
        queryFn: () => (tenantId ? adminApi.getTenantStorage(tenantId, token) : Promise.resolve(null)),
        enabled: Boolean(tenantId) && Boolean(token)
    });
}

export function useConnectPolicy(tenantId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "connect-policy", tenantId, token],
        queryFn: () => (tenantId ? adminApi.getConnectPolicy(tenantId, token) : Promise.resolve(null)),
        enabled: Boolean(tenantId) && Boolean(token)
    });
}

export function useUpdateConnectPolicy(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: ConnectPolicy) =>
            adminApi.updateConnectPolicy(tenantId, payload, token),
        onSuccess: (data) => {
            qc.setQueryData(["admin", "connect-policy", tenantId, token], data);
            void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
        }
    });
}

// SAML identity providers (phase 1: registration + validation).

export function useSamlIdps(tenantId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "saml-idps", tenantId, token],
        queryFn: () => (tenantId ? adminApi.listSamlIdps(tenantId, token) : Promise.resolve([])),
        enabled: Boolean(tenantId) && Boolean(token)
    });
}

const invalidateSamlIdps = (qc: ReturnType<typeof useQueryClient>, tenantId: string) => {
    void qc.invalidateQueries({ queryKey: ["admin", "saml-idps", tenantId] });
    void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
};

export function useCreateSamlIdp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertSamlIdpPayload) => adminApi.createSamlIdp(tenantId, p, token),
        onSuccess: () => invalidateSamlIdps(qc, tenantId)
    });
}

export function useUpdateSamlIdp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertSamlIdpPayload }) =>
            adminApi.updateSamlIdp(tenantId, args.id, args.payload, token),
        onSuccess: () => invalidateSamlIdps(qc, tenantId)
    });
}

export function useValidateSamlIdp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => adminApi.validateSamlIdp(tenantId, id, token),
        onSuccess: () => invalidateSamlIdps(qc, tenantId)
    });
}

export function useDeleteSamlIdp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => adminApi.deleteSamlIdp(tenantId, id, token),
        onSuccess: () => invalidateSamlIdps(qc, tenantId)
    });
}

// Per-registration, not global — see SamlIdentityProvider's class remarks: each registration has
// its own ACS URL, so its SP metadata is only meaningful once a registration is selected.
export function useSpMetadataSummary(id: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "saml-sp-metadata-summary", id, token],
        queryFn: () => (id ? adminApi.getSpMetadataSummary(id, token) : Promise.resolve(null)),
        enabled: Boolean(id) && Boolean(token)
    });
}

// SAML service providers — the reverse role (third-party apps trusting Athavita as their IdP).

export function useSamlSps(tenantId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "saml-sps", tenantId, token],
        queryFn: () => (tenantId ? adminApi.listSamlSps(tenantId, token) : Promise.resolve([])),
        enabled: Boolean(tenantId) && Boolean(token)
    });
}

const invalidateSamlSps = (qc: ReturnType<typeof useQueryClient>, tenantId: string) => {
    void qc.invalidateQueries({ queryKey: ["admin", "saml-sps", tenantId] });
    void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
};

export function useCreateSamlSp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: UpsertSamlSpPayload) => adminApi.createSamlSp(tenantId, p, token),
        onSuccess: () => invalidateSamlSps(qc, tenantId)
    });
}

export function useUpdateSamlSp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { id: string; payload: UpsertSamlSpPayload }) =>
            adminApi.updateSamlSp(tenantId, args.id, args.payload, token),
        onSuccess: () => invalidateSamlSps(qc, tenantId)
    });
}

export function useValidateSamlSp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => adminApi.validateSamlSp(tenantId, id, token),
        onSuccess: () => invalidateSamlSps(qc, tenantId)
    });
}

export function useDeleteSamlSp(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => adminApi.deleteSamlSp(tenantId, id, token),
        onSuccess: () => invalidateSamlSps(qc, tenantId)
    });
}

// Per-registration — each registration has its own SSO URL (see SamlServiceProvider's class
// remarks), so this is only meaningful once a registration is selected.
export function useIdpMetadataSummary(id: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "saml-idp-metadata-summary", id, token],
        queryFn: () => (id ? adminApi.getIdpMetadataSummary(id, token) : Promise.resolve(null)),
        enabled: Boolean(id) && Boolean(token)
    });
}

// Mobile devices & app protection (Mobile/docs/phase0-contract.md §4 "Admin").

export function useMobileDevices(q: MobileDeviceQuery) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "mobile-devices", q.tenantId ?? "all", q.userId ?? "all", q.status ?? "any", q.skip, q.take, token],
        queryFn: () => mobileApi.listDevices(q, token),
        enabled: Boolean(token),
        // Keep the current page on screen while the next one loads.
        placeholderData: keepPreviousData
    });
}

export function useMobileDeviceCommand() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { deviceId: string; command: MobileDeviceCommand; tenantId: string | null | undefined }) =>
            mobileApi.deviceCommand(args.deviceId, args.command, args.tenantId, token),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["admin", "mobile-devices"] });
            void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
        }
    });
}

export function useMobilePolicy(tenantId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["admin", "mobile-policy", tenantId, token],
        queryFn: () => (tenantId ? mobileApi.getPolicy(tenantId, token) : Promise.resolve(null)),
        enabled: Boolean(tenantId) && Boolean(token)
    });
}

export function useUpdateMobilePolicy(tenantId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (policy: MobilePolicy) => mobileApi.updatePolicy(tenantId, policy, token),
        onSuccess: (data) => {
            if (data) qc.setQueryData(["admin", "mobile-policy", tenantId, token], data);
            else void qc.invalidateQueries({ queryKey: ["admin", "mobile-policy", tenantId] });
            void qc.invalidateQueries({ queryKey: ["admin", "audit"] });
        }
    });
}
